import React, { useEffect } from 'react';
import { useLocation } from "react-router-dom";
import {motion } from "framer-motion";
import { smoothScrollToElement } from "../utils/smoothScroll";
import { Link } from 'react-router-dom';
import { FaRegCircleCheck,
  FaClock, 
  FaCalendarDays, 
  FaDollarSign, 
  FaCalendarCheck, 
  FaHourglassStart,
  FaWhatsapp,
  FaQuestion
} from "react-icons/fa6";
import { GiHeartShield, GiAura, GiFaceToFace, GiArcheryTarget } from "react-icons/gi";
import GuatemalaFlag from "../assets/flags/gt.svg";
import MexicoFlag from "../assets/flags/mx.svg";
import UsFlag from "../assets/flags/us.svg";
import International from "../assets/flags/world-map.svg";
import Teddy from "../assets/teddy-bear.svg";
import { GrWorkshop } from "react-icons/gr";
import './Services.css';

type ServiceProps = {
  onOpen: () => void;
};

const Services: React.FC<ServiceProps> = ({onOpen}) => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;

    setTimeout(() => {
      smoothScrollToElement(el, { offset: 100, duration: 1500 }); 
    }, 0);
  }, [hash]);

  const services = [
    {
      icon: GiAura,
      title: `Psicoterapia Integral Individual`,
      description: "Un espacio personal y confidencial para explorar emociones, pensamientos y conductas. Te ayuda a manejar el estrés, la ansiedad y los retos diarios, favoreciendo el crecimiento personal y el bienestar emocional.",
      details: [
        'Trauma psicológico (PTSD)',
        'Trauma complejo (PTSD -C)',
        'Disociación',
        'Apego inseguros ',
        'Ansiedad',
        'Depresión',
        'Manejo de Estrés',
        'Gestión emocional',
        'Valor personal (autoestima, autoconcepto, etc)',
        'Procesar las pérdidas (Duelos)',
        'Trastornos de personalidad',
        'Estrés laboral, burnout y work - life balance',
      ],
      duration: "50 minutos",
      price: "$ 60",
      sessions: "Semanal/Quincenal"
    },
    {
      icon: GiFaceToFace,
      title: "Terapia Conductual Integrativa de Pareja",
      description: "Un espacio seguro para fortalecer la relación, mejorar la comunicación y manejar los conflictos de manera constructiva. Ayuda a las parejas a comprender sus diferencias, fomentar la empatía y construir vínculos más saludables y satisfactorios.",
      details: [
        "Problemas de comunicación",
        "Conflictos recurrentes",
        "Crisis de Pareja",
        "Celos y control",
        "Estilos de apego",
        "Heridas emocionales",
        "Separación",
        "Divorcio",
        "Entre otros."
      ],
      duration: "50 minutos",
      price: "$ 60",
      sessions: "Semanal/Quincenal"
    },
    {
      icon: GiHeartShield,
      title: "Psicoterapia en Cuidado Paliativos",
      description: "Acompañamiento emocional para pacientes y familias en etapas avanzadas de enfermedad. Manejo del miedo, la ansiedad y el dolor emocional, favoreciendo la tranquilidad, la comunicación y la calidad de vida en un entorno de respeto y dignidad.",
      details: [
        "Afrontamiento del diagnóstico y la enfermedad avanzada.",
        "Ansiedad",
        "Depresión",
        "Sentido de la vida.",
        "Espiritualidad",
        "Pérdida de autonomía e identidad.",
        "Apoyo en la elaboración psicoemocional ante la proximidad del fallecimiento.",
      ],
      duration: "50 minutos",
      price: "$ 60",
      sessions: "Semanal/Quincenal"
    },
    {
      icon: GiArcheryTarget,
      title: "Terapia Centrada en el Trauma",
      description: "Un proceso especializado para sanar experiencias dolorosas del pasado. Ayuda a comprender y manejar el impacto emocional del trauma, promoviendo seguridad, resiliencia y bienestar en la vida diaria.",
      details: [
        "Procesamiento del evento traumático",
        "Primeros auxilios psicológicos",
        "Regulación emocional",
        "Síntomas de trastorno de estrés postraumático (TEPT)",
        "Vínculos y relaciones interpersonales",
        "Sentido y significado",
        "Recuperación del cuerpo",
        "Sensación de seguridad"
      ],
      duration: "50 minutos",
      price: "$ 60",
      sessions: "Semanal/Quincenal"
    },
    {
      icon: GrWorkshop,
      title: "Talleres y Charlas para Empresas",
      description: "Acompañamiento profesional personalizado que busca obtener un alto rendimiento en la empresa, impulsando el desarrollo integral de los colaboradores y del equipo de trabajo.",
      details: [
        "Talleres y Retos personalizados para empresas, marcas y grupos",
        "Consultorías & Massterclasses para organizaciones",
        "Reclutamiento de persona",
        "Coaching para empresas",
        "Mejora del liderazgo",
        "Aumento del rendimiento individual y de equipos",
        "Comunicación efectiva",
        "Gestión del cambio",
        "Mejora del clima laboral",
        "Desarrollo de talento"
      ],
      duration: "Personalizada",
      price: "¡Solicita una cotización!",
      sessions: "Programa Personalizado"
    }
  ];

  return (
    <div className="services">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content text-center">
            <h1>¿Qué servicios ofrezco? </h1>
            <p className="services-hero-subtitle">
              Aprende a amarte y sabrás amar.

            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section section-light">
        <div className="container">
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-header">
                  <service.icon className="service-card-icon" size={48} />
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
                <div className="service-details">
                  <ul>
                    {service.details.map((detail, idx) => (
                      <li key={idx}>
                        <FaRegCircleCheck size={16} className="check-icon" />
                        {detail}
                      </li>
                    ))
                    }
                  </ul>
                </div>
                <div className="service-info">
                  <div className="service-info-item">
                    <FaClock size={20} />
                    <span><strong>Duración:</strong> {service.duration}</span>
                  </div>
                  <div className="service-info-item">
                    <FaDollarSign size={22} />
                    <span><strong>Precio:</strong>  {service.price}</span>
                  </div>
                  <div className="service-info-item">
                    <FaCalendarDays size={20} />
                    <span> <strong>Frecuencia:</strong> {service.sessions}</span>
                  </div>
                </div>
                <span className='disclaimer'>* Los precios varían según el país/región </span>
                <div className="service-actions">
                  <Link to="/contact" className="btn btn-primary">
                    <FaCalendarCheck className='icon-small' /> Reservar Cita
                  </Link>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section section-dark">
        <div className="container" id="first-step">
          <div className="section-header text-center">
            <h2>¿Cuál es mi forma de trabajar? </h2>
            <p className="section-subtitle">
              En esta sección encontrarás a <strong>detalle</strong> el <i>paso</i> a <i>paso</i> para poder <strong>iniciar</strong> con tu proceso.
              ¿Listo para dar el primer paso?
            </p>
          </div>
          <div className="methodology-content">
            <div className="methodology-grid">
              <div className="methodology-step">
                <div className="step-number"><FaHourglassStart /></div>
                <h4>Antes de iniciar... </h4>
                <p>
                  ¡Muy importante! <strong>Descarga</strong> y lee el <strong>consentimiento informado</strong> correspondiente a tu país.
                </p>
                <Link to="/services#files" className='btn btn-primary'>Descargar </Link>
              </div>
              <div className="methodology-step">
                <div className="step-number">1</div>
                <h4>Acuerdo y Firma</h4>
                <p>
                  Una vez que hayas leído <strong>cuidadosamente</strong> el documento, y estes de <i>acuerdo con mi forma de trabajo</i>, <strong>fírmalo</strong>.
                </p>
              </div>
              <div className="methodology-step">
                <div className="step-number">2</div>
                <h4>Completa tu Ficha de Inscripción</h4>
                <p>
                  Una vez firmado el <strong>consentimiento informado</strong>, completa la ficha de Inscripción y adjunta el documento.
                </p>
                <motion.button className='btn btn-primary' onClick={onOpen}>Completar</motion.button>
              </div>
              <div className="methodology-step">
                <div className="step-number">3</div>
                <h4>Agendamos tu Primer Sesión</h4>
                <p>
                  Listo, ya has firmado y completado tu Inscripción. Ahora si, 
                  agendamos tu <strong>primer sesión</strong> el día y horario de conveniencia.
                </p>
              </div>
              <div className="methodology-step">
                <div className="step-number">4</div>
                <h4>Evaluación Inicial</h4>
                <p>
                  En tu primer sesión, realizo una evaluación completa para entender tu situación,
                  establecer objetivos terapéuticos claros y diseñar un plan
                  de tratamiento personalizado.
                </p>
              </div>
              <div className="methodology-step">
                <div className="step-number">5</div>
                <h4>Seguimiento y Ajuste</h4>
                <p>
                  <strong>Intervención Terapéutica</strong>, monitoreamos el progreso continuamente, ajustando el
                  tratamiento según sea necesario para garantizar
                  los mejores resultados.
                </p>
              </div>
              <div className="methodology-step">
                <div className="step-number">6</div>
                <h4>Mantenimiento</h4>
                <p>
                  Proporciono herramientas y estrategias para mantener
                  los logros alcanzados y prevenir futuras recaídas. Material premium y Programas personalizados.
                </p>
              </div>
            </div>
          </div>
          <div className="section-header text-center files-header" id="files">
            <h2>Consentimientos Informados</h2>
            <p className="section-subtitle">
              Estos documentos protegen los derechos del paciente/cliente y dan transparencia al profesional.
            </p>
          </div>
          <div className="files-content">
            <div className="files-grid">
              <div className="file">
                <span><img src={GuatemalaFlag} alt="Bandera de Guatemala" className="icon-large" /></span>
                <h4>Consentimiento Informado Guatemala </h4>
                <a
                  href="/docs/consentimiento-informado-Guatemala.pdf"
                  download="Consentimiento-Informado-Guatemala.pdf"
                  className="btn btn-primary"
                >
                  Descargar PDF
                </a>
              </div>
              <div className="file">
                <span><img src={MexicoFlag} alt="Bandera de Mexico" className="icon-large" /></span>
                <h4>Consentimiento Informado México </h4>
                <a
                  href="/docs/consentimiento-informado-Mexico.pdf"
                  download="Consentimiento-Informado-Mexico.pdf"
                  className="btn btn-primary"
                >
                  Descargar PDF
                </a>
              </div>
              <div className="file">
                <span><img src={UsFlag} alt="Bandera de Estados Unidos" className="icon-large" /></span>
                <h4>Consentimiento Informado Estados Unidos </h4>
                <a
                  href="/docs/consentimiento-informado-US.pdf"
                  download="Consentimiento-Informado-US.pdf"
                  className="btn btn-primary"
                >
                  Descargar PDF
                </a>
              </div>
              <div className="file">
                <span><img src={International} alt="International" className="icon-large" /></span>
                <h4>Consentimiento Informado Internacional </h4>
                <a
                  href="/docs/consentimiento-informado-Internacional.pdf"
                  download="Consentimiento-Informado-Internacional.pdf"
                  className="btn btn-primary"
                >
                  Descargar PDF
                </a>
              </div>
              <div className="file">
                <span><img src={Teddy} alt="Infantil" className="icon-large" /></span>
                <h4>Consentimiento Informado Infantil </h4>
                <a
                  href="/docs/consentimiento-informado-Infantil.pdf"
                  download="Consentimiento-Informado-Infantil.pdf"
                  className="btn btn-primary"
                >
                  Descargar PDF
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>Preguntas Frecuentes</h2>
            <p className="section-subtitle">
              Resuelvo las dudas más comunes sobre mis servicios psicológicos
            </p>
          </div>
          <div className="faq-content">
            <div className="faq-item">
              <h4>¿Qué es un conocimiento informado?</h4>
              <p>
                Un documento (y un proceso de conversación) mediante el cual una persona
                acepta voluntariamente recibir un servicio o intervención —por ejemplo, atención psicológica— después
                de entender de qué se trata, sus beneficios, riesgos, alternativas y el manejo de su información.
              </p>
            </div>
            <div className="faq-item">
              <h4>¿Cuánto dura una sesión de terapia?</h4>
              <p>
                Todas las sesiones (individuales, de pareja, PAP) duran 50 minutos. Loa talleres y charlas para empresas
                tienen una duración personalizada.
              </p>
            </div>
            <div className="faq-item">
              <h4>¿Con qué frecuencia necesito asistir a terapia?</h4>
              <p>
                La frecuencia depende de tus necesidades específicas. Generalmente
                recomiendo sesiones semanales al inicio del tratamiento, que pueden
                espaciarse a quincenales según el progreso.
              </p>
            </div>
            <div className="faq-item">
              <h4>¿Cuánto tiempo dura un tratamiento psicológico?</h4>
              <p>
                La duración varía según el problema a tratar y los objetivos establecidos.
                Algunos tratamientos pueden requerir 8-10 sesiones, mientras que otros
                pueden necesitar un seguimiento más prolongado.
              </p>
            </div>
            <div className="faq-item">
              <h4>¿La terapia online es tan efectiva como la presencial?</h4>
              <p>
                Sí, estudios científicos demuestran que la terapia online es igual de
                efectiva que la presencial para muchos trastornos. Utilizo plataformas
                seguras que garantizan la confidencialidad.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="services-cta-content text-center">
            <h2>¿Necesitas más información?</h2>
            <p>
              Estoy aquí para ayudarte. No dudes en contactarme para resolver
              cualquier duda sobre mis servicios o para agendar tu primera consulta.
            </p>
            <div className="services-cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                <FaQuestion className="icon" />
                Hacer Consulta
              </Link>
              <a
                href="https://wa.me/34123456789?text=Hola%2C%20me%20gustar%C3%ADa%20información%20sobre%20sus%20servicios"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <FaWhatsapp className="icon" />
                Consultar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;